import React, { useState, useEffect, useCallback, Fragment } from 'react';
import styled from 'styled-components'
import { Button } from '@components'
import { ReactComponent as Contract } from '@assets/Icon_Contract.svg';
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import { ReactComponent as Check } from '@assets/Icon_Check.svg';
import { ReactComponent as ErrorIcon } from '@assets/Icon_Error.svg';
import { ReactComponent as IconCancel } from '@assets/Icon_Cancel.svg';
import assetController from '@app/App.Controller'

const Statuses = {
	NOTAPPROVED: 'NOTAPPROVED',
	APPROVING: 'APPROVING',
	APPROVED: 'APPROVED',
	IDLE: "IDLE"
}

const ApprovalTrigger = ({ name, toApprove, onContract, amount, onApproval }) => {

	const { tokens } = assetController

	const [status, setStatus] = useState(Statuses.NOTAPPROVED)
	const [text, setText] = useState(`${amount === 0 ? 'Unapprove' : 'Approve'} ${name}`)

	const handleApproval = async () => {
		setStatus(Statuses.APPROVING)
		setText(<Fragment><Refresh className='rotate' />&nbsp;&nbsp;{amount === 0 ? 'Unapproving' : 'Approving'} {name}...</Fragment>)

		tokens
			.approve(toApprove, onContract, amount)
			.then(() => {
				setStatus(Statuses.APPROVED)
				setText(<Fragment><Check />&nbsp;&nbsp;{amount === 0 ? 'Unapproved' : 'Approved'} {name}</Fragment>)
				onApproval()
			})
			.catch(msg => {
				setStatus(Statuses.NOTAPPROVED)
				setText(<Fragment><ErrorIcon />&nbsp;&nbsp;{amount === 0 ? 'Unapprove' : 'Approve'} {name}</Fragment>)
			})
	}

	useEffect(() => {
		setText(`${amount === 0 ? 'Unapprove' : 'Approve'} ${name}`)
	}, [name, amount])

	return <Button
		disabled={status !== Statuses.NOTAPPROVED}
		onClick={handleApproval}
		small
	>
		{text}
	</Button>
}

// This is for v3, should remove old one once finished the upgration
const ApprovalTriggerNew = styled(
	({ className, name, approvedItems, onContract, toApprove, onApproval, disabled = false }) => {

		const { tokens } = assetController

		const [text, setText] = useState(name)
		const [status, setStatus] = useState(Statuses.APPROVED)

		useEffect(() => {

			if (approvedItems && approvedItems.find(item => item.name === name)) {
				setStatus(Statuses.IDLE)
				setText(`Approve ${name}`)
			} else {

				setStatus(Statuses.APPROVED)
				setText(`${name}`)
			}
		}, [name, approvedItems])

		const handleApproval = () => {
			setStatus(Statuses.APPROVING)
			setText(`Approving ${name}...`)

			tokens
				.approve(toApprove, onContract)
				.then(() => {
					setStatus(Statuses.APPROVED)
					setText(`Approved ${name}`)
					onApproval()
				})
				.catch(msg => {
					setStatus(Statuses.NOTAPPROVED)
					setText(`Approve ${name}`)
				})
		}

		return (
			<Button.Full
				className={`${className} -approve-button`}
				onClick={handleApproval}
				disabled={status === Statuses.APPROVED || status === Statuses.APPROVING}
			>
				{!disabled && (
					<>
						{status === Statuses.APPROVED && <Check />}
						{status === Statuses.APPROVING && <Refresh className='rotate' />}
						{status === Statuses.NOTAPPROVED && <ErrorIcon />}
					</>
				)}

				<div style={{ margin: "auto" }}>
					{text}
				</div>
			</Button.Full>
		)
	})`


	`


const useCounter = (initialCount = 0) => {
	const [count, setCount] = useState(initialCount);
	const increment = useCallback(() => setCount(c => c + 1), []);
	const decrement = useCallback(() => setCount(c => c - 1), []);
	return { count, increment, decrement };
}

const Banner = styled(
	({ approvals = [], onUpdate = () => { }, onApproved = () => { }, className, ...rest }) => {
		const { tokens } = assetController

		const [displayState, setDisplayState] = useState('closed')
		const [itemsForApproval, setItemsForApproval] = useState()
		const [title, setTitle] = useState('Checking token approvals...')

		const {
			count: approvedCount,
			increment: approveOne
		} = useCounter(0);
		// update approvals when tokens are updated
		useEffect(() => {
			if (approvals.length > 0) defineApprovals(approvals)
		}, [approvals])  // eslint-disable-line


		// trigger callbacks on approval count changes
		useEffect(() => {
			let timeout = setTimeout(() => { }, 1)

			if (itemsForApproval) {
				onUpdate({ total: itemsForApproval.length, approved: approvedCount })
				if (approvedCount >= itemsForApproval.length) {
					onApproved()
					timeout = setTimeout(() => setDisplayState('closed'), 2000)
				}
			}

			return () => clearTimeout(timeout)
		}, [approvedCount, itemsForApproval]) // eslint-disable-line


		const defineApprovals = async _approvals => {
			onUpdate({ total: Object.values(_approvals).length, approved: 0 })

			let items = await Promise.all(
				_approvals.map(async approval => {
					let approved = await tokens.isApproved(approval.toApprove, approval.onContract)
					return !approved ? { ...approval } : null

					// hey dev, uncomment to remove approval
					//return { ...approval, amount: 0 }
				})
			)

			items = items.filter(e => e)

			onUpdate({ total: items.length, approved: approvedCount })

			if (items.length > 0) {
				setDisplayState('awaiting')
				setItemsForApproval(items)
				setTitle('Token approval required to proceed')
			} else {
				onApproved()
				setTitle('No tokens to approve')
				//setTimeout(() => setDisplayState('closed'), 1000)
				//setDisplayState('closed')
			}
		}

		return <span
			className={`asset-approval ${className}`}
			data-state={displayState}
			{...rest}
		>
			<span className='-title'>
				<Contract />
				<span>{title}</span>
			</span>
			<span className='-buttons'>
				{itemsForApproval && itemsForApproval.map((item, i) =>
					<ApprovalTrigger
						key={i}
						{...item}
						onApproval={approveOne}
					/>
				)}
			</span>
		</span>
	})`
	
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0;
	max-height: 0;
	overflow: hidden;
	transition: all 0.5s;

	>span{
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	>.-title{
		color: var(--color-label);
		svg{
			margin-right: 0.4em;
			color: inherit;
		}
	}

	svg.check{
		color: var(--color-status-success, green)
	}

	&[data-state="pending"],
	&[data-state="awaiting"]{
		padding: 1.4rem 0;
		max-height: 5rem;
	}
	`

const ApprovalButton = ({ total = -1, approved = -1, children, onClick, ...rest }) => {
	const [text, setText] = useState(`Checking approvals`)

	useEffect(() => {
		if (approved < total || approved < total) {
			setText(`Awaiting approval${total > 1 ? 's' : ''} (${approved}/${total})`)
		} else {
			setText(children)
		}
	}, [total, approved]) // eslint-disable-line

	return <Button.Primary
		disabled={approved < total}
		onClick={() => approved < total ? () => { } : onClick()}
		{...rest}
	>
		{text}
	</Button.Primary>
}


const Dot = styled.div`
	height: 26px;
	width: 26px;
	border-radius: 50%;
	display: flex;
	font-size: 12px;
	font-weight: 700;
	color: blue;
	div {
		margin: auto;
	}
	
	${ props => props.active
		?
		`
		background-color: #FFFFFF;
		color: #2D6AE2;
	`
		:
		`
		background-color: rgba(255, 255, 255, 0.2);
		color: #FFFFFF;
	`}

`


const Wizard = styled(
	({
		className,
		disabled,
		approvals = [],
		onUpdate = () => { },
		onApproved = () => { },
		actionText = "",
		onAction = () => { },
	}) => {

		const { tokens } = assetController

		const [itemsForApproval, setItemsForApproval] = useState()

		const {
			count: approvedCount,
			increment: approveOne
		} = useCounter(0);

		// update approvals when tokens are updated
		useEffect(() => {
			if (approvals.length > 0) defineApprovals(approvals)
		}, [approvals, approvedCount])  // eslint-disable-line

		// trigger callbacks on approval count changes
		useEffect(() => {
			let timeout = setTimeout(() => { }, 1)

			if (itemsForApproval) {
				onUpdate({ total: itemsForApproval.length, approved: approvedCount })
				if (approvedCount >= itemsForApproval.length) {
					onApproved()
				}
			}

			return () => clearTimeout(timeout)
		}, [approvedCount, itemsForApproval]) // eslint-disable-line

		const defineApprovals = async _approvals => {
			onUpdate({ total: Object.values(_approvals).length, approved: 0 })

			let items = await Promise.all(
				_approvals.map(async approval => {
					let approved = await tokens.isApproved(approval.toApprove, approval.onContract)
					return !approved ? { ...approval } : null
				})
			)

			items = items.filter(e => e)

			onUpdate({ total: items.length, approved: approvedCount })

			if (items.length > 0) {
				setItemsForApproval(items)
			} else {
				onApproved()
				setItemsForApproval()
			}
		}

		const currentStep = ((approvals.length !== 0) && (!disabled)) ? approvals.reduce((result, item) => {
			if (itemsForApproval && itemsForApproval.find(approvalItem => approvalItem.name === item.name)) {
				result = 1
			}
			return result
		}, 2) : 0

		return (
			<div className={className}>
				<div className="-steps">
					{[1, 2].map((step, index) => {
						return (
							<div className="-step" key={index}>
								<div>
									<Dot
										active={currentStep === step}
									>
										<div>{step}</div>
									</Dot>
								</div>
								<div>
									{step === 1 &&
										(<div className="-approvals">
											{approvals && approvals.map((item, i) => {
												return (
													<ApprovalTriggerNew
														key={i}
														name={item.name}
														toApprove={item.toApprove}
														onContract={item.onContract}
														approvedItems={itemsForApproval}
														onApproval={approveOne}
														disabled={disabled}
													/>
												)
											})}
											{disabled && approvals.length === 0 && (
												<Button.Full disabled={disabled} className="-approve-button">
													Token not selected
												</Button.Full>
											)}
										</div>)}
									{step === 2 &&
										(
											<Button.Full
												disabled={currentStep !== 2}
												className="-action-button"
												onClick={onAction}
											>
												{actionText}
											</Button.Full>
										)}
								</div>
							</div>
						)
					})}
				</div>
			</div>
		)
	})`
	display: flex;
	flex-direction: column;

	.-approve-button {
		font-size: 16px;
		width: 100%;
		height: 40px;
		color: white;
		background: transparent;
		display: flex;
		flex-direction: row;
		border: 1px solid rgba(255,255,255,0.2);
		:hover{ 
			border: 1px solid rgba(255,255,255,0.8);
		}
	}

	>.-steps {

		>.-step {
			flex-direction: row;
			display: flex;
			height: 40px;
			margin-top: 10px;
			margin-bottom: 10px;
			justify-content: center;
			align-items: center;
			>div { 
				flex: 90%;
				:first-child {
					flex: 10%;
				}
				.-action-button {
					font-size: 16px;
					width: 100%;
					height: 40px;
				}
				.-approvals {
					display: flex;
					flex-direction: row;
					div {
						flex: 1;
					}
				}
				

			}
			
		}
		
	}

	`


Banner.Button = ApprovalButton
Banner.Trigger = ApprovalTrigger
Banner.Wizard = Wizard

export default Banner